import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, AlertCircle, Info } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { AuthButton } from './AuthButton';

import { bearerAuthHeader } from '../utils/apiHeaders';
export function UserRecovery() {
  const [email, setEmail] = useState('');
  const [diagnosticResults, setDiagnosticResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  const runDiagnostic = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setDiagnosticResults(null);
    setRecoverySuccess(false);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/user-diagnostic`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...bearerAuthHeader(),
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Diagnostic failed');
        setLoading(false);
        return;
      }

      setDiagnosticResults(data);
    } catch (err: any) {
      console.error('Diagnostic error:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const recoverUser = async () => {
    setError('');
    setLoading(true);
    setRecoverySuccess(false);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/recover-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...bearerAuthHeader(),
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Recovery failed');
        setLoading(false);
        return;
      }

      setRecoverySuccess(true);
      setDiagnosticResults(null);
    } catch (err: any) {
      console.error('Recovery error:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 p-6">
      <div className="flex justify-end mb-4">
        <AuthButton />
      </div>
      <div className="flex items-center justify-center">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>User Account Recovery</CardTitle>
            <CardDescription>
              Diagnose and fix user account issues after system updates
            </CardDescription>
          </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Use this tool if:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>You can't log in after the subdomain feature was added</li>
                <li>You get "User not found" errors</li>
                <li>Your email isn't recognized by the system</li>
              </ul>
            </AlertDescription>
          </Alert>

          <form onSubmit={runDiagnostic} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Your Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Running Diagnostic...' : 'Check Account Status'}
            </Button>
          </form>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {diagnosticResults && (
            <div className="space-y-4">
              <Alert className={diagnosticResults.authExists ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
                <AlertDescription>
                  <strong>Supabase Auth Account:</strong>{' '}
                  {diagnosticResults.authExists ? (
                    <span className="text-green-700">Found (ID: {diagnosticResults.authUserId})</span>
                  ) : (
                    <span className="text-red-700">Not found</span>
                  )}
                </AlertDescription>
              </Alert>

              <Alert className={diagnosticResults.kvUserExists ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}>
                <AlertDescription>
                  <strong>User Record in Database:</strong>{' '}
                  {diagnosticResults.kvUserExists ? (
                    <span className="text-green-700">Found</span>
                  ) : (
                    <span className="text-yellow-700">Missing (this is the problem)</span>
                  )}
                  {diagnosticResults.kvUser && (
                    <div className="mt-2 space-y-1">
                      <div>Role: <strong>{diagnosticResults.kvUser.role}</strong></div>
                      <div>Company ID: <strong>{diagnosticResults.kvUser.companyId || 'None (System Admin)'}</strong></div>
                      <div>Status: <strong>{diagnosticResults.kvUser.status}</strong></div>
                    </div>
                  )}
                </AlertDescription>
              </Alert>

              {diagnosticResults.authExists && !diagnosticResults.kvUserExists && (
                <Alert className="border-blue-500 bg-blue-50">
                  <Info className="h-4 w-4 text-blue-700" />
                  <AlertDescription className="text-blue-800">
                    <strong>Problem Identified:</strong> Your auth account exists, but the user record is missing.
                    This can happen after system updates. Click the button below to recreate your user record.
                  </AlertDescription>
                </Alert>
              )}

              {!diagnosticResults.authExists && (
                <Alert className="border-orange-500 bg-orange-50">
                  <AlertCircle className="h-4 w-4 text-orange-700" />
                  <AlertDescription className="text-orange-800">
                    <strong>Account Not Found:</strong> No auth account exists for this email.
                    You'll need to use the bootstrap setup to create a system admin account,
                    or ask an existing admin to create an account for you.
                  </AlertDescription>
                </Alert>
              )}

              {diagnosticResults.authExists && !diagnosticResults.kvUserExists && (
                <Button 
                  onClick={recoverUser} 
                  className="w-full" 
                  disabled={loading}
                  variant="default"
                >
                  {loading ? 'Recovering Account...' : 'Recover My User Record'}
                </Button>
              )}
            </div>
          )}

          {recoverySuccess && (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Account Recovered</strong><br />
                Your user record has been recreated. You can now{' '}
                <a href="/" className="underline font-semibold">return to login</a>.
              </AlertDescription>
            </Alert>
          )}

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              <a href="/" className="underline hover:text-foreground">← Back to Login</a>
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}